import React, { useMemo, useState } from 'react';
import Button from '../Common/Button';
import FindingCard from './FindingCard';
import './ReviewRoom.css';

function splitParagraphs(draft) {
  return (draft || '').split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
}

/**
 * S3 — the review room, the heart of the Editing Room (Unit 4 build spec
 * §D1-D3). Draft pane (Spectral) + findings panel: side-by-side at desktop
 * widths, the panel collapsing to a summonable drawer at narrow widths —
 * one responsive behavior (CSS), not two variant components (design brief
 * §8's deviation #5). Hover/tap a finding softly highlights its quoted
 * paragraph; nothing else marks up the prose.
 */
export default function ReviewRoom({
  draft, wordCount, modelName, generatedAt,
  findings, advisoryFindings, rulings, onRule,
  editing, onToggleEdit, editedDraft, onDraftChange,
  onAcceptAsIs, onRecheckEdits, onRegenerate,
  regenerateEstimate, busy, drawerOpenDefault, onDraftFresh,
}) {
  const [hoveredId, setHoveredId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(drawerOpenDefault ?? true);
  const [note, setNote] = useState('');

  const paragraphs = useMemo(() => splitParagraphs(editing ? editedDraft : draft), [editing, editedDraft, draft]);
  const hoveredFinding = useMemo(
    () => [...findings, ...advisoryFindings].find((f) => f.id === hoveredId),
    [hoveredId, findings, advisoryFindings],
  );
  const highlightedParagraphIndex = useMemo(() => {
    if (!hoveredFinding || editing) return -1;
    return paragraphs.findIndex((p) => p.includes(hoveredFinding.quote));
  }, [hoveredFinding, paragraphs, editing]);

  const agreedCount = findings.filter((f) => rulings[f.id] === 'agree').length;
  const hasNote = note.trim().length > 0;
  // Regenerate is valid with EITHER an agreed finding OR a note — a note
  // alone is a real instruction to act on, not nothing (punch-list §2.4).
  const canRegenerate = (agreedCount > 0 || hasNote) && !busy && !editing;

  const tallyLine = agreedCount === 0 && !hasNote
    ? 'Nothing marked yet — findings you agree with will be addressed.'
    : regenerateEstimate && !regenerateEstimate.missing_pricing
      ? `est. $${regenerateEstimate.estimate_usd.toFixed(2)} · not to exceed $${regenerateEstimate.not_to_exceed_usd.toFixed(2)}`
      : agreedCount > 0
        ? `Addressing ${agreedCount} finding${agreedCount === 1 ? '' : 's'}.`
        : 'Addressing your note.';

  const regenerateLabel = agreedCount === 0 && !hasNote
    ? 'Regenerate'
    : agreedCount > 0
      ? `Regenerate addressing ${agreedCount} finding${agreedCount === 1 ? '' : 's'}`
      : 'Regenerate with your note';

  const totalFindingCount = findings.length + advisoryFindings.length;

  return (
    <div className="er-review">
      <div className="er-review__main">
        <div className="er-review__prose-wrap">
          <div className="er-review__prose-col">
            <div className="er-review__meta">
              <span className="er-review__meta-line">
                Draft · {wordCount.toLocaleString()} words{modelName ? ` · ${modelName}` : ''}{generatedAt ? ` · ${generatedAt}` : ''}
              </span>
            </div>
            {editing && (
              <div className="er-review__editnote">
                You&rsquo;re editing. Your words are yours — nothing re-runs, nothing is checked, nothing is charged.
              </div>
            )}
            <div className={`er-review__prose${editing ? ' er-review__prose--editing' : ''}`} data-testid="er-prose">
              {editing ? (
                <textarea
                  className="er-review__textarea"
                  value={editedDraft}
                  onChange={(e) => onDraftChange(e.target.value)}
                  aria-label="Edit the draft"
                />
              ) : (
                paragraphs.map((p, i) => (
                  <p
                    key={i}
                    className={`er-review__para${i === highlightedParagraphIndex ? ' er-review__para--highlight' : ''}`}
                  >
                    {p}
                  </p>
                ))
              )}
            </div>
          </div>
        </div>

        {!editing && (
          <div className="er-review__note-row">
            <label className="er-review__note-label" htmlFor="er-review-note">Anything else for this pass?</label>
            <input
              id="er-review-note"
              className="er-review__note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional — rides the rewrite as your own instruction, recorded on the receipt"
              disabled={busy}
            />
          </div>
        )}

        <div className="er-review__actionbar">
          <Button variant="secondary" size="medium" disabled={busy || editing} onClick={onAcceptAsIs}>
            Accept as-is
          </Button>
          <button type="button" className="er-review__edit-btn" disabled={busy} onClick={onToggleEdit}>
            {editing ? 'Done editing' : 'Edit draft'}
          </button>
          {editing && (
            <button type="button" className="er-review__recheck-btn" disabled={busy} onClick={onRecheckEdits}>
              Re-check my edits
              {regenerateEstimate && !regenerateEstimate.missing_pricing && ` · est. $${regenerateEstimate.estimate_usd.toFixed(2)}`}
            </button>
          )}
          {!editing && (
            <>
              <div className="er-review__divider" />
              <span className="er-review__tally">{tallyLine}</span>
              <Button
                variant="primary" size="medium" disabled={!canRegenerate} loading={busy}
                onClick={() => onRegenerate(note.trim())}
              >
                {regenerateLabel}
              </Button>
            </>
          )}
          {onDraftFresh && (
            <button type="button" className="er-review__draft-fresh" disabled={busy} onClick={onDraftFresh}>
              Draft fresh
            </button>
          )}
        </div>
      </div>

      <button
        type="button"
        className="er-review__drawer-toggle"
        onClick={() => setDrawerOpen((o) => !o)}
      >
        {drawerOpen ? 'Hide findings' : 'Findings'} · {totalFindingCount}
      </button>

      <div className={`er-review__panel${drawerOpen ? ' er-review__panel--open' : ''}${editing ? ' er-review__panel--quiet' : ''}`}>
        <div className="er-review__panel-head">
          <div className="er-review__panel-title-row">
            <span className="er-review__panel-title">Findings</span>
            <span className="er-review__panel-count">{totalFindingCount} · from inspectors</span>
          </div>
          <div className="er-review__panel-sub">The inspector advises. You rule.</div>
        </div>
        <div className="er-review__panel-list">
          {totalFindingCount === 0 && (
            <div className="er-review__panel-empty">No findings — nothing to rule on.</div>
          )}
          {findings.map((f) => (
            <FindingCard
              key={f.id} finding={f} advisory={false} ruling={rulings[f.id]}
              disabled={busy || editing}
              onHover={() => setHoveredId(f.id)} onLeave={() => setHoveredId(null)}
              onRule={onRule}
            />
          ))}
          {advisoryFindings.map((f) => (
            <FindingCard
              key={f.id} finding={f} advisory disabled={busy || editing}
              onHover={() => setHoveredId(f.id)} onLeave={() => setHoveredId(null)}
              onRule={onRule}
            />
          ))}
        </div>
        <div className="er-review__panel-foot">
          Overruled findings stay on the record — excluded from the regeneration, not deleted.
        </div>
      </div>
    </div>
  );
}
